// src/modules/app-views/products/pages/Document/tabs/StockTab/index.jsx
// *******************************************************************************
// PRODUCTS STOCK TAB
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback } from 'react';
import { Row, Col, Collapse, Button, Form } from 'antd';
import {
  SwapOutlined,
  PlusOutlined,
  ScanOutlined,
  AppstoreOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import SimpleSwitch from 'components/SimpleComponents/SimpleSwitch';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import translateX from 'utils/translateX';
import { openModal } from 'components/core/GenericModal';
import StockCard from './StockCard';
import AddressCard from './AddressCard';
import StockMovementsCard from './StockMovementsCard';
import TraceabilityContent from './TraceabilityContent';
import ProductGridContent from './ProductGridContent';
import StockMovementModal from '../../modals/StockMovementModal';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const CONTAINER_STYLE = { position: 'relative', minHeight: 120 };
const SWITCH_WRAPPER_STYLE = { marginBottom: 8 };
const COLLAPSE_STYLE = { marginTop: 8, marginBottom: 12 };
const ICON_MARGIN_STYLE = { marginRight: 6 };
const ROW_STRETCH_STYLE = { alignItems: 'stretch' };
const COL_FLEX_STYLE = { display: 'flex' };
const FLEX_1_STYLE = { flex: 1 };
const LABEL_SPAN_STYLE = { display: 'inline-flex', alignItems: 'center', height: 40 };
const COLLAPSE_STYLES = {
  header: { alignItems: 'center' },
  body: { padding: 20 },
};

const FADE_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ProductsStockTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, loading, refresh, viewMode } = useDocumentContext();
  const { form } = useSimpleFormContext() || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const trackInventoryForm = Form.useWatch(['stock', 'trackInventory'], form);
  // Use form value if defined, otherwise fallback to saved data, default true
  const isTracking = trackInventoryForm !== undefined
    ? trackInventoryForm !== false
    : data?.stock?.trackInventory !== false;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // const [identificationKeys, setIdentificationKeys] = useState(['identification']);
  const [identificationKeys, setIdentificationKeys] = useState([]);
  const [movementsKeys, setMovementsKeys] = useState([]);
  const [traceabilityKeys, setTraceabilityKeys] = useState([]);
  const [gridKeys, setGridKeys] = useState([]);
  const [refreshToken, setRefreshToken] = useState(0);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAddMovement = useCallback(
    e => {
      e.stopPropagation();
      openModal(StockMovementModal, {
        productId: data?._id,
        stockQuantity: data?.stock?.quantity || 0,
        allowNegativeStock: data?.stock?.allowNegativeStock || false,
        onSuccess: () => {
          setRefreshToken(prev => prev + 1);
          refresh?.();
        },
      });
    },
    [data?._id, data?.stock?.quantity, data?.stock?.allowNegativeStock, refresh],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container" style={CONTAINER_STYLE}>
      <SimpleLoadingArea name="productStock" isFetching={loading} data={data} />

      {/* Track Inventory switch — top right, inline horizontal */}
      <div style={SWITCH_WRAPPER_STYLE}>
        <SimpleSwitch
          name={['stock', 'trackInventory']}
          label="track_inventory"
          inline
        />
      </div>

      {/* All stock panels — fade in/out based on trackInventory */}
      <AnimatePresence mode="wait">
        {isTracking && (
          <motion.div
            key="stock-panels"
            variants={FADE_VARIANTS}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            {/* Identification — starts open */}
            <Collapse
              size="small"
              style={COLLAPSE_STYLE}
              styles={COLLAPSE_STYLES}
              activeKey={identificationKeys}
              onChange={keys => setIdentificationKeys(keys)}
              items={[
                {
                  key: 'identification',
                  forceRender: true,
                  label: buildCollapseLabel(
                    <IdcardOutlined style={ICON_MARGIN_STYLE} />,
                    translateX('identification'),
                  ),
                  children: (
                    <Row gutter={[16, 16]} style={ROW_STRETCH_STYLE}>
                      <Col xs={24} lg={12} style={COL_FLEX_STYLE}>
                        <StockCard style={FLEX_1_STYLE} />
                      </Col>
                      <Col xs={24} lg={12} style={COL_FLEX_STYLE}>
                        <AddressCard style={FLEX_1_STYLE} />
                      </Col>
                    </Row>
                  ),
                },
              ]}
            />

            {/* Stock Movements — starts closed */}
            <Collapse
              size="small"
              style={COLLAPSE_STYLE}
              styles={COLLAPSE_STYLES}
              activeKey={movementsKeys}
              onChange={keys => setMovementsKeys(keys)}
              items={[
                {
                  key: 'movements',
                  forceRender: true,
                  label: buildCollapseLabel(
                    <SwapOutlined style={ICON_MARGIN_STYLE} />,
                    translateX('stock_movements'),
                  ),
                  extra: viewMode === 'edit' ? (
                    <Button
                      type="default"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={handleAddMovement}
                    />
                  ) : null,
                  children: <StockMovementsCard refreshToken={refreshToken} onRefresh={refresh} />,
                },
              ]}
            />

            {/* Traceability — starts closed */}
            <Collapse
              size="small"
              style={COLLAPSE_STYLE}
              styles={COLLAPSE_STYLES}
              activeKey={traceabilityKeys}
              onChange={keys => setTraceabilityKeys(keys)}
              items={[
                {
                  key: 'traceability',
                  forceRender: true,
                  label: buildCollapseLabel(
                    <ScanOutlined style={ICON_MARGIN_STYLE} />,
                    translateX('traceability'),
                  ),
                  children: <TraceabilityContent />,
                },
              ]}
            />

            {/* Product Grid — starts closed */}
            <Collapse
              size="small"
              style={COLLAPSE_STYLE}
              styles={COLLAPSE_STYLES}
              activeKey={gridKeys}
              onChange={keys => setGridKeys(keys)}
              items={[
                {
                  key: 'productGrid',
                  forceRender: true,
                  label: buildCollapseLabel(
                    <AppstoreOutlined style={ICON_MARGIN_STYLE} />,
                    translateX('product_grid'),
                  ),
                  children: <ProductGridContent />,
                },
              ]}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ProductsStockTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildCollapseLabel(icon, text) {
  return (
    <span style={LABEL_SPAN_STYLE}>
      {icon}
      {text}
    </span>
  );
}
